import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileModel, ListResponse, ListSearch, Result, WorkflowSimple } from '@ca/core/models';
import { BehaviorSubject, Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MEDResultDto } from '../models/med-result-dto.model';
import { ArivisService } from './arivis.service';
import { ExperimentalFeaturesService } from './experimental-features.service';
import { FileService } from './file.service';
import { RepositoryService } from './repository-service/repository.service';
import { ResultsService } from './results/results.service';
import { WorkflowRunQueueService } from './workflow-run-queue.service';
import { WorkflowV1Service } from './workflow-v1.service';

@Injectable()
export class UserWorkspaceService {
    hasGitCredentials = false;
    gitName = '';
    isMyFilesLoading$: Observable<boolean>;
    myFiles$: Observable<FileModel[]>;
    myFilesTotalCount$: Observable<number>;
    myFilesFilteredSearchResultCount$: Observable<number>;
    myWorkflows$: Observable<WorkflowSimple[]>;
    myWorkflowsTotalCount$: Observable<number>;
    myResults$: Observable<Result[] | MEDResultDto[]>;
    myResultsTotalCount$: Observable<number>;

    private myFiles$$ = new BehaviorSubject<FileModel[]>([]);
    private myFilesTotalCount$$ = new BehaviorSubject(0);
    private myFilesFilteredSearchResultsCount$$ = new BehaviorSubject(0);
    private isMyFilesLoading$$ = new BehaviorSubject(false);
    private myWorkflows$$ = new BehaviorSubject<WorkflowSimple[]>([]);
    private myWorkflowsTotalCount$$ = new BehaviorSubject(0);
    private myResults$$ = new BehaviorSubject<Result[] | MEDResultDto[]>([]);
    private myResultsTotalCount$$ = new BehaviorSubject(0);

    constructor(
        private fileService: FileService,
        private repositoryService: RepositoryService,
        private resultsService: ResultsService,
        private workflowV1Service: WorkflowV1Service,
        private workflowRunQueueService: WorkflowRunQueueService,
        private arivisService: ArivisService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {
        this.isMyFilesLoading$ = this.isMyFilesLoading$$.asObservable();
        this.myFiles$ = this.myFiles$$.asObservable();
        this.myFilesTotalCount$ = this.myFilesTotalCount$$.asObservable();
        this.myFilesFilteredSearchResultCount$ = this.myFilesFilteredSearchResultsCount$$.asObservable();
        this.myWorkflows$ = this.myWorkflows$$.asObservable();
        this.myWorkflowsTotalCount$ = this.myWorkflowsTotalCount$$.asObservable();
        this.myResults$ = this.myResults$$.asObservable();
        this.myResultsTotalCount$ = this.myResultsTotalCount$$.asObservable();
        this.workflowRunQueueService.workflowCompleted$.pipe(tap((): void => this.handleWorkflowFinishedEvent())).subscribe();
    }

    /**
     * Fetches the user's Git credentials and updates the service properties with the retrieved values.
     * @returns {Observable<{ name: string }>} An observable emitting the Git username or an error.
     */
    getGitCredentials$(): Observable<{ name: string }> {
        return this.repositoryService.getUserNameInfo().pipe(
            tap(({ name }: { name: string }): void => {
                if (name) {
                    this.hasGitCredentials = !!name;
                    this.gitName = name;
                }
            }),
            catchError((err: unknown): Observable<never> => {
                this.hasGitCredentials = false;
                this.gitName = '';
                return throwError(err);
            }),
        );
    }

    /**
     * Updates the list of workflows associated with the user.
     * Fetches the data and updates the observable streams accordingly.
     * @returns {Observable<void>} An observable that completes when the update is done.
     */
    updateMyWorkflows$(): Observable<void> {
        const params = new HttpParams()
            .set('page[number]', '1')
            .set('page[size]', '7')
            .set('sort', '-createdAt')
            .set('includeOwn', 'true')
            .set('includePublic', 'false');

        return this.workflowV1Service.getPrivateOverviewList(params).pipe(
            map((listResponse: ListResponse<WorkflowSimple>): void => {
                this.myWorkflows$$.next(listResponse.data);
                this.myWorkflowsTotalCount$$.next(listResponse?.meta?.totalRecordCount ?? 0);
            }),
            catchError((err: unknown): Observable<never> => {
                return throwError(err);
            }),
        );
    }

    /**
     * Updates the list of results associated with the user.
     * Fetches results based on project type (MED or non-MED) and updates observables.
     * @returns {Observable<void>} An observable that completes when the update is done.
     */
    updateMyResults$(): Observable<void> {
        const params = new HttpParams()
            .set('sort', '-finishedAt')
            .set('page[number]', '1')
            .set('page[size]', '1')
            .set('title', '')
            .set('text', '')
            .set('groupId', '');

        if (!this.experimentalFeaturesService.isMEDProjectActive) {
            return this.resultsService.search$(params).pipe(
                map((listResponse: ListResponse<Result>): void => {
                    this.myResults$$.next(listResponse.data);
                    this.myResultsTotalCount$$.next(listResponse?.meta?.totalCount ?? 0);
                }),
                catchError((err: unknown): Observable<never> => {
                    return throwError(err);
                }),
            );
        } else {
            return this.resultsService.searchGroupMED$(params).pipe(
                map((listResponse: ListResponse<MEDResultDto>): void => {
                    this.myResults$$.next(listResponse.data);
                    this.myResultsTotalCount$$.next(listResponse?.meta?.totalCount ?? 0);
                }),
                catchError((err: unknown): Observable<never> => {
                    return throwError(err);
                }),
            );
        }
    }

    /**
     * Updates the list of files in the user's workspace.
     * Filters files based on provided search parameters and updates observables.
     * @param {ListSearch} [searchParams] - Optional search parameters for filtering files.
     * @returns {Observable<void>} An observable that completes when the update is done.
     */
    updateMyFiles$(searchParams?: ListSearch): Observable<void> {
        const searchTerm = searchParams?.searchQuery || '';
        this.isMyFilesLoading$$.next(true);

        return this.fileService.getFilesWithMetadata$(searchParams).pipe(
            map(({ data, meta }: ListResponse<FileModel>): void => {
                this.myFiles$$.next(data.map((file: FileModel): FileModel => this.buildFile(file)));
                this.myFilesFilteredSearchResultsCount$$.next(meta?.totalCount ?? 0);

                if (searchTerm === '') {
                    this.myFilesTotalCount$$.next(meta?.totalCount ?? 0);
                }
                this.isMyFilesLoading$$.next(false);
            }),
            catchError((err: unknown): Observable<never> => {
                this.isMyFilesLoading$$.next(false);
                return throwError(err);
            }),
        );
    }

    /**
     * Updates all data in the user's workspace including files, workflows, results, and git credentials.
     * Combines multiple observables to execute updates in parallel.
     * @returns {Observable<[void, void, void, { name: string }]>}
     * Observable emitting an array of update results for workflows, results, files, and git credentials.
     */
    update$(): Observable<[void, void, void, { name: string }]> {
        this.hasGitCredentials = false;
        this.gitName = '';

        return combineLatest([
            this.updateMyWorkflows$(),
            this.updateMyResults$(),
            this.updateMyFiles$(),
            this.getGitCredentials$().pipe(catchError((): Observable<{ name: string }> => of({ name: '' }))),
        ]);
    }

    /**
     * Constructs a new `FileModel` object with additional metadata and viewer URLs.
     * @param {FileModel} file - The original file model.
     * @returns {FileModel} A new file model with enriched properties.
     */
    private buildFile(file: FileModel): FileModel {
        const newFile = new FileModel(file.id, file.token, file.createdAt, file.storageType, file.sizeInBytes, file.name, file.fullpath, file.type);

        newFile.workflowResultMetadata = file.workflowResultMetadata;
        newFile.shareUrl = this.arivisService.generateArivisViewerUrl(file.token);
        newFile.type = file.type;
        newFile.viewerUrl = this.arivisService.generateArivisMlViewerUrl(file);
        newFile.workflowRunId = file.workflowRunId;

        return newFile;
    }

    /**
     * Handles events when a workflow is finished.
     * Triggers an update of the user's results.
     * @private
     */
    private handleWorkflowFinishedEvent(): void {
        this.updateMyResults$().subscribe();
    }
}
